"use client";

import type { Affiliation, Organization, User } from "@airollplay/database";
import { z } from "@airollplay/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { type SignInResponse, signIn } from "next-auth/react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FormSubmitButton } from "@/components/form-submit-button";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { isMobile } from "@/lib/user-agent/is-mobile";

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type FieldInputs = z.input<typeof schema>;
type FieldOutputs = z.output<typeof schema>;

export function SignInForm({
  affiliation,
  browserName,
  isBrowserAvailable,
}: {
  affiliation:
    | (Affiliation & {
        organization: Organization;
        user: User;
      })
    | null;
  browserName: string | undefined;
  isBrowserAvailable: boolean;
}) {
  const router = useRouter();

  const searchParams = useSearchParams();

  const [isBusy, setIsBusy] = useState(false);

  const form = useForm<FieldInputs, unknown, FieldOutputs>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: affiliation?.user.email || searchParams.get("email") || "",
      password: "",
    },
  });

  const submit = useCallback(
    async (values: FieldOutputs) => {
      try {
        setIsBusy(true);

        const result = await toast.promise(
          signIn("credentials", {
            ...values,
            ...(affiliation ? { affiliation: affiliation.id } : {}),
            redirect: false,
          }),
          {
            loading: "認証情報を確認しています...",
            error: (err: unknown) => `${err}`,
            success: (result: SignInResponse | undefined) => {
              if (!result?.ok || result?.error) {
                throw new Error("サインインに失敗しました");
              }

              return "サインインに成功しました";
            },
          },
        );

        if (result?.ok && !result?.error) {
          const parsedSearchParams = z
            .object({
              redirect: z.string().optional(),
            })
            .parse(Object.fromEntries(searchParams.entries()));

          if (await isMobile()) {
            router.push(parsedSearchParams.redirect || "/mobile");
          } else {
            router.push(parsedSearchParams.redirect || "/user");
          }

          router.refresh();
        }
      } catch {
        toast.error("サインインに失敗しました");
      } finally {
        setIsBusy(false);
      }
    },
    [affiliation, router, searchParams],
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="flex flex-col gap-4">
        {affiliation ? (
          <FormItem>
            <FormLabel>組織</FormLabel>
            <FormControl>
              <Input
                readOnly={true}
                value={affiliation.organization.name}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        ) : null}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="username"
                  readOnly={affiliation !== null}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>パスワード</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <hr />

        {isBrowserAvailable ? (
          <FormSubmitButton
            label="サインイン"
            isBusy={isBusy}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <Button
              variant="outline"
              disabled={true}
              className="border-destructive">
              <div className="flex flex-col justify-center items-center">
                <div className="text-sm text-destructive">
                  非対応のブラウザです
                </div>
              </div>
            </Button>

            <div className="text-center text-xs text-destructive">
              Chromeブラウザからログインしてください
              <br />
              ご利用のブラウザ：{browserName}
            </div>
          </div>
        )}

        <Button
          asChild
          variant="link">
          <Link
            href={[
              "/password-reset-requests",
              `?${new URLSearchParams({
                ...(affiliation
                  ? {
                      a: affiliation.id,
                      email: affiliation.user.email,
                    }
                  : {}),
              }).toString()}`,
            ].join("")}>
            パスワードがわからない方はこちら
          </Link>
        </Button>
      </form>
    </Form>
  );
}
